/**
 * Process assignments — admins only; shown on the Configuration tab of the
 * feature that owns the processes (Business Opportunities today).
 *
 * Which model each AI process runs on. Every module calls the shared AI
 * service with its process key; this is where the model behind that key is
 * chosen. Every process needs one — there is no fallback model — and only
 * usable models (enabled, configured) can be picked. The connection test is
 * never listed: it always runs on the model being tested. What a process
 * sends is on its feature's Prompt section, not here. Any usable model of the
 * process's capability can be picked: the app runs in the client's own
 * environment, so the models an admin registers are already the allowed ones.
 * A refused assignment's reason shows above the table.
 */

import { AlertTriangle, Workflow } from 'lucide-react';

import { useModels, useProcesses } from '@/api/queries';
import type { AiModel, AiProcess } from '@/api/types';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { SaveBar } from '@/ui/SaveBar';
import { useSession } from '@/session/SessionContext';

import { useAssignmentsDraft } from './useAssignmentsDraft';

export function ProcessAssignments({ processKeys }: { processKeys: readonly string[] }) {
  const models = useModels();
  const processes = useProcesses();

  if (models.isPending || processes.isPending) return <LoadingCard label="Loading process assignments" />;
  const failure = models.error ?? processes.error;
  if (failure) {
    return (
      <LoadFailure
        error={failure}
        onRetry={() => {
          void models.refetch();
          void processes.refetch();
        }}
      />
    );
  }

  if (!models.data || !processes.data) return null;
  const shown = processes.data.filter((p) => processKeys.includes(p.key));
  return <AssignmentsCard shown={shown} models={models.data} />;
}

function AssignmentsCard({ shown, models }: { shown: AiProcess[]; models: AiModel[] }) {
  const editable = useSession().can('ai.processes.manage');
  const draft = useAssignmentsDraft(shown);
  const usable = models.filter((m) => m.unusable_reason === null);
  const modelOf = (key: string | null) => models.find((m) => m.key === key);

  return (
    <div className="card">
      {draft.failures.length > 0 && (
        <div className="alert alert-warning form-alert card-alert" role="alert">
          <div className="a-body">
            <div>Could not save: {draft.failures.map((f) => f.name).join(', ')}.</div>
            {[...new Set(draft.failures.map((f) => f.message))].map((message) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        </div>
      )}
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th scope="col">Module</th>
              <th scope="col">Process</th>
              <th scope="col">Model</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((process) => (
              <ProcessRow
                key={process.key}
                process={process}
                assigned={modelOf(process.model_key)}
                usable={usable}
                editable={editable}
                draft={draft}
              />
            ))}
          </tbody>
        </table>
      </div>
      {editable && (
        <div className="card-body">
          <SaveBar changed={draft.changed} state={draft.state} onReset={draft.reset} onSave={() => void draft.save()} />
        </div>
      )}
    </div>
  );
}

type Draft = ReturnType<typeof useAssignmentsDraft>;

function ProcessRow({
  process,
  assigned,
  usable,
  editable,
  draft,
}: {
  process: AiProcess;
  assigned: AiModel | undefined;
  usable: AiModel[];
  editable: boolean;
  draft: Draft;
}) {
  const describedBy = process.problem ? `problem-${process.key}` : undefined;
  return (
    <tr>
      <td><span className="badge badge-neutral">{process.module}</span></td>
      <td>
        <span className="row process-name">
          <Workflow aria-hidden="true" />
          <span>
            <b>{process.name}</b>
            <span className="sub">{process.description}</span>
          </span>
        </span>
      </td>
      <td>
        {editable ? (
          <ModelSelect process={process} assigned={assigned} usable={usable} describedBy={describedBy} draft={draft} />
        ) : (
          <span aria-describedby={describedBy}>{assigned ? assigned.display_name : 'No model assigned'}</span>
        )}
        {process.problem && (
          <span id={describedBy} className="row status-warn process-problem">
            <AlertTriangle aria-hidden="true" /> {process.problem}
          </span>
        )}
      </td>
    </tr>
  );
}

function ModelSelect({
  process,
  assigned,
  usable,
  describedBy,
  draft,
}: {
  process: AiProcess;
  assigned: AiModel | undefined;
  usable: AiModel[];
  describedBy: string | undefined;
  draft: Draft;
}) {
  // An assigned model that became unusable stays visible, so the select never looks blank.
  const fits = (m: AiModel) => m.capability === process.capability;
  const stale = assigned && (assigned.unusable_reason !== null || !fits(assigned)) ? assigned : null;
  // The server refuses a model of the wrong capability, so only models it would accept are offered.
  const choices = usable.filter(fits);
  return (
    <select
      className="process-select"
      aria-label={`Model for ${process.name}`}
      aria-describedby={describedBy}
      value={draft.valueOf(process)}
      // aria-disabled, not disabled: a focused select keeps focus while the save runs, and changes are ignored.
      aria-disabled={draft.saving || undefined}
      onChange={(e) => !draft.saving && draft.pick(process, e.target.value)}
    >
      {process.model_key === null && <option value="" disabled>Select a model…</option>}
      {stale && <option value={stale.key} disabled>{stale.display_name} (unavailable)</option>}
      {choices.map((m) => (
        <option key={m.key} value={m.key}>{m.display_name}</option>
      ))}
    </select>
  );
}
